import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from 'react-datepicker';
import sv from 'date-fns/locale/sv';
import { Tooltip } from '../common/tooltip.component';

// Swedish locale for the calendar labels (date-fns ships with react-datepicker).
registerLocale('sv', sv);

// Portal the calendar popper to <body> so no ancestor with overflow:hidden /
// transform can clip it.
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  ReactDOM.createPortal(children, document.body);

// Bridge the parent's ISO-string contract (yyyy-mm-dd) ↔ react-datepicker's Date.
const parseIso = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface EmployerSignatureProps {
  date: string;
  onDateChange: (date: string) => void;
  signatureDataUrl: string | null;
  onSignatureChange: (dataUrl: string | null) => void;
  nameClarification: string;
  onNameClarificationChange: (value: string) => void;
  onSignatureModeChange?: (isDigitally: boolean) => void;
  uploadedFileName?: string;
  onUploadedFileNameChange?: (name: string) => void;
  errors?: { date?: string; signature?: string; name?: string };
}

export const EmployerSignature: React.FC<EmployerSignatureProps> = ({
  date,
  onDateChange,
  signatureDataUrl,
  onSignatureChange,
  nameClarification,
  onNameClarificationChange,
  onSignatureModeChange,
  onUploadedFileNameChange,
  errors,
}) => {
  const { t, i18n } = useTranslation();
  const calendarLocale = i18n.language?.toLowerCase().startsWith('sv') ? 'sv' : undefined;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [drawMode, setDrawMode] = useState(false);
  // Blob URL for reliably previewing an uploaded image (the base64 data URL is
  // still stored via onSignatureChange for submission). Tracked in a ref so it
  // can be revoked without adding deps to the memoized handlers.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const revokePreview = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  // Revoke any outstanding blob URL on unmount.
  useEffect(() => () => revokePreview(), [revokePreview]);

  // Sync canvas internal pixel size to its CSS display size, accounting for DPR
  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== Math.round(rect.width * dpr) || canvas.height !== Math.round(rect.height * dpr)) {
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);
    }
  }, []);

  // Sync size on mount and on window resize
  useEffect(() => {
    syncCanvasSize();
    const observer = new ResizeObserver(() => syncCanvasSize());
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [syncCanvasSize]);

  // Get accurate canvas-relative position accounting for DPR
  const getPos = (e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  useEffect(() => {
    if (!drawMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    syncCanvasSize();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const startDrawing = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      isDrawingRef.current = true;
      const pos = getPos(e, canvas);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const draw = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!isDrawingRef.current) return;
      const pos = getPos(e, canvas);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const stopDrawing = () => {
      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;
      ctx.beginPath();
      onSignatureChange(canvas.toDataURL());
    };

    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseleave', stopDrawing);
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing);

    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseleave', stopDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
    };
  }, [drawMode, onSignatureChange, syncCanvasSize]);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    revokePreview();
    setPreviewUrl(null);
    onSignatureChange(null);
  }, [onSignatureChange, revokePreview]);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type — only PNG / JPG / JPEG (same rule as the logo upload).
    const fileFormat = file.name.split('.').pop()?.toLowerCase();
    if (!file.type.startsWith('image/') || !(fileFormat === 'png' || fileFormat === 'jpg' || fileFormat === 'jpeg')) {
      toast.error(t('LOGO_FORMAT_NOT_SUPPORTED') as string);
      e.target.value = '';
      return;
    }

    // Validate file size (max 1MB).
    if (file.size > 1024 * 1024) {
      toast.error(t('FILE_TOO_LARGE_1MB') as string);
      e.target.value = '';
      return;
    }

    // Preview from a blob URL (reliable for local files); clear any drawn strokes.
    revokePreview();
    const objectUrl = URL.createObjectURL(file);
    objectUrlRef.current = objectUrl;
    setPreviewUrl(objectUrl);
    setDrawMode(false);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    onSignatureModeChange?.(true); // uploaded = isDigitally true
    onUploadedFileNameChange?.(file.name);

    // Still store the base64 data URL — submission strips the prefix and sends it.
    const reader = new FileReader();
    reader.onload = (ev) => {
      onSignatureChange(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleDraw = () => {
    setDrawMode(true);
    onSignatureModeChange?.(false); // drawn = isDigitally false
    onUploadedFileNameChange?.('');
    revokePreview();
    setPreviewUrl(null);
    if (!signatureDataUrl) return;
    handleClear();
  };

  return (
    <div className="ec-signature-panel">
      <h4 className="ec-signature-panel-title">
        {t('ON_BEHALF_OF_EMPLOYER') || 'On behalf of the employer:'}
      </h4>

      <div className="ec-field-group">
        <label className="ec-field-label">{t('DATE') || 'Date'}</label>
        <div className="ec-dp-wrap">
          <DatePicker
            selected={parseIso(date)}
            onChange={(d: Date | null) => onDateChange(toIso(d))}
            dateFormat="yyyy/MM/dd"
            placeholderText="yyyy/mm/dd"
            maxDate={new Date()}
            className={`ec-field-input${errors?.date ? ' ec-field-input--error' : ''}`}
            wrapperClassName="ec-dp__input-dp"
            popperContainer={PopperPortal}
            popperProps={{ strategy: 'fixed' }}
            popperClassName="ec-dp__popper"
            calendarClassName="ec-dp__calendar"
            onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
            showMonthDropdown
            showYearDropdown
            dropdownMode="scroll"
            locale={calendarLocale}
            autoComplete="off"
          />
          <span className="ec-dp-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4.5" width="18" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 9.5h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
        </div>
        {errors?.date && <p className="ec-validation-error">{t(errors.date)}</p>}
      </div>

      <div className="ec-field-group">
        <div className="ec-label-row">
          <label className="ec-field-label">{t('SIGNATURE') || 'Signature'}</label>
          <Tooltip content={t('EC_SIGNATURE_UPLOAD_INFO', { defaultValue: 'Draw your signature in the box, or upload one as a PNG or JPG image (max 1 MB).' }) as string}>
            <span className="ec-info-icon" tabIndex={0} aria-label={t('EC_SIGNATURE_UPLOAD_INFO', { defaultValue: 'Signature upload details' }) as string}>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="7.6" r="1.1" fill="currentColor" />
              </svg>
            </span>
          </Tooltip>
        </div>
        <div className={`ec-signature-canvas-wrapper${drawMode ? ' ec-signature-canvas-wrapper--active' : ''}`}>
          <canvas
            ref={canvasRef}
            className="ec-signature-canvas"
          />
          {!drawMode && (previewUrl || signatureDataUrl) && (
            <img
              src={(previewUrl || signatureDataUrl) as string}
              alt={t('SIGNATURE') || 'Signature'}
              className="ec-signature-preview-img"
            />
          )}
          {!drawMode && !signatureDataUrl && (
            <span className="ec-signature-canvas-placeholder">
              {t('CLICK_DRAW_TO_SIGN') || 'Click "Draw Signature" to sign here'}
            </span>
          )}
        </div>
        <div className="ec-signature-actions">
          <button type="button" className="ec-signature-btn ec-signature-btn--draw" onClick={handleDraw}>
            {t('DRAW_SIGNATURE') || 'Draw Signature'}
          </button>
          <button type="button" className="ec-signature-btn ec-signature-btn--upload" onClick={() => fileInputRef.current?.click()}>
            {t('UPLOAD_SIGNATURE') || 'Upload Signature'}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
        </div>
        {(signatureDataUrl || drawMode) && (
          <button type="button" className="ec-signature-clear-btn" onClick={handleClear}>
            {t('CLEAR') || 'Clear'}
          </button>
        )}
        {errors?.signature && <p className="ec-validation-error">{t(errors.signature)}</p>}
      </div>

      <div className="ec-field-group">
        <label className="ec-field-label">{t('NAME_CLARIFICATION') || 'Name Clarification'}</label>
        <input
          className="ec-field-input"
          type="text"
          value={nameClarification}
          onChange={(e) => onNameClarificationChange(e.target.value)}
          placeholder={t('ENTER_NAME_CLARIFICATION') || 'Enter name clarification...'}
        />
        {errors?.name && <p className="ec-validation-error">{t(errors.name)}</p>}
      </div>
    </div>
  );
};
